import PropTypes from 'prop-types';
import {merge} from 'lodash';
import validator from 'validator';
import {
  getResource,
  getResourceMetadata,
  getResourcePromise,
  invalidateInterest,
  isResourcePopulated,
  isResourceReady,
  isResourceValid,
  buildEndpoint
} from 'resources/mandark.resource';
import type {ResourceQueryConfigurationType} from 'resources/mandark.resource';

/**
 * Constructor shape for a `defaultModel` carried on the query config. The model resolver layer
 * hands these back as `unknown`; within this builder they're only ever used via `new`.
 */
type ModelConstructor = new (...args: unknown[]) => unknown;

/**
 * The query config object that flows through the builder. It is assembled incrementally (each
 * chained method merges in another slice), so every field is optional here. mandark.resource's
 * functions expect a fully-populated {@link ResourceQueryConfigurationType}; at those call sites
 * the runtime guarantees the relevant fields exist, so we assert the narrower type.
 */
type ResourceConfig = Partial<ResourceQueryConfigurationType> & {
  defaultModel?: ModelConstructor;
};

export type QueryBuilder = {
  customQuery: (...args: unknown[]) => QueryBuilder;
  customHeader: (...args: unknown[]) => QueryBuilder;
  defaultModel: (defaultModel: ModelConstructor) => QueryBuilder;
  done: () => ResourceConfig;
  fields: (...args: unknown[]) => QueryBuilder;
  equals: (objToCompare: QueryBuilder) => boolean;
  filter: (...args: unknown[]) => QueryBuilder;
  findOne: (...args: unknown[]) => QueryBuilder;
  getResource: () => unknown;
  getResourceMetadata: () => unknown;
  getResourcePromise: () => Promise<unknown>;
  include: (...args: unknown[]) => QueryBuilder;
  invalidateInterest: () => unknown;
  isResourcePopulated: () => boolean;
  isResourceReady: () => boolean;
  isResourceValid: () => boolean;
  meta: (...args: unknown[]) => QueryBuilder;
  page: (...args: unknown[]) => QueryBuilder;
  pageSize: (...args: unknown[]) => QueryBuilder;
  sort: (...args: unknown[]) => QueryBuilder;
  with: (...args: unknown[]) => QueryBuilder;
  withMeta: (...args: unknown[]) => QueryBuilder;
};

function val(a: unknown, b: boolean | null | undefined, c: unknown): unknown {
  return b ? a : c;
}

/**
 * The transforms returned by the helper functions below are invoked by {@link invoke} with the
 * caller-supplied argument (a filter object, page id, sort string, etc.) — genuinely dynamic
 * data — and return the next query config.
 */
type ConfigTransform = (arg: unknown) => ResourceConfig;

function filter(obj: ResourceConfig): ConfigTransform {
  return (filterParam) => query(obj).with({filter: filterParam}).done();
}

function findOne(obj: ResourceConfig): ConfigTransform {
  return () =>
    query(obj)
      .with({resourcePath: obj.resourcePath?.unshift('lookup')})
      .done();
}

function customQuery(obj: ResourceConfig): ConfigTransform {
  return (queryParam) => query(obj).with({customQuery: queryParam}).done();
}

function customHeader(obj: ResourceConfig): ConfigTransform {
  return (header) => query(obj).with({customHeader: header}).done();
}

function fields(obj: ResourceConfig): ConfigTransform {
  return (fieldsParam) => customQuery(obj)({fields: fieldsParam});
}

function page(obj: ResourceConfig): ConfigTransform {
  return (pageParam) => {
    const pageQuery = validator.isUUID(String(pageParam)) ? {id: pageParam} : {page: pageParam};
    return customQuery(obj)({page: pageQuery});
  };
}

function pageSize(obj: ResourceConfig): ConfigTransform {
  return (pageSizeParam) => customQuery(obj)({page: {page_size: pageSizeParam}});
}

function sort(obj: ResourceConfig): ConfigTransform {
  return (sortParam) => customQuery(obj)({sort: sortParam});
}

function meta(obj: ResourceConfig): ConfigTransform {
  return (customMeta) => customQuery(obj)({meta: customMeta});
}

function withMeta(obj: ResourceConfig): ConfigTransform {
  return (str) => customQuery(obj)({with_meta: str});
}

function includes(obj: ResourceConfig): ConfigTransform {
  return (str) => {
    const include = str as string;
    return obj.include
      ? Object.assign({}, obj, {include: `${obj.include},${include}`})
      : Object.assign({}, obj, {include});
  };
}

function invoke(...args: unknown[]): (func: ConfigTransform) => QueryBuilder {
  const obj = args[0] as ResourceConfig;
  const a = args[1];
  const b = args.length === 3 ? Boolean(args[2]) : true;

  const v = val(a, b, obj);
  // When `v === obj`, `v` is the (unchanged) config; otherwise `func` maps the caller-supplied
  // argument `v` to the next config. Either branch yields a `ResourceConfig`.
  return (func) => (v === obj ? query(v as ResourceConfig) : query(func(v)));
}

export function query(obj: ResourceConfig = {}): QueryBuilder {
  return {
    with: (...args) => invoke(obj, ...args)((merger) => merge(merger, obj)),
    filter: (...args) => invoke(obj, ...args)(filter(obj)),
    findOne: (...args) => invoke(obj, ...args)(findOne(obj)),
    customQuery: (...args) => invoke(obj, ...args)(customQuery(obj)),
    fields: (...args) => invoke(obj, ...args)(fields(obj)),
    page: (...args) => invoke(obj, ...args)(page(obj)),
    pageSize: (...args) => invoke(obj, ...args)(pageSize(obj)),
    sort: (...args) => invoke(obj, ...args)(sort(obj)),
    meta: (...args) => invoke(obj, ...args)(meta(obj)),
    withMeta: (...args) => invoke(obj, ...args)(withMeta(obj)),
    include: (...args) => invoke(obj, ...args)(includes(obj)),
    customHeader: (...args) => invoke(obj, ...args)(customHeader(obj)),
    done: () => obj,
    // Mandark resource methods below
    defaultModel: (defaultModel) => query(merge(obj, {defaultModel})),
    getResource: () => {
      return obj.defaultModel && !isResourcePopulated(obj as ResourceQueryConfigurationType)
        ? new obj.defaultModel()
        : getResource(obj as ResourceQueryConfigurationType);
    },
    getResourceMetadata: () => getResourceMetadata(obj as ResourceQueryConfigurationType),
    getResourcePromise: async () => {
      return await getResourcePromise(obj as ResourceQueryConfigurationType);
    },
    invalidateInterest: () => invalidateInterest(obj as ResourceQueryConfigurationType),
    isResourcePopulated: () => isResourcePopulated(obj as ResourceQueryConfigurationType),
    isResourceReady: () => isResourceReady(obj as ResourceQueryConfigurationType),
    isResourceValid: () => isResourceValid(obj as ResourceQueryConfigurationType),
    equals: (objToCompare) =>
      buildEndpoint(obj as ResourceQueryConfigurationType) ===
      buildEndpoint(objToCompare.done() as ResourceQueryConfigurationType)
  };
}

export function mandarkEndpoint(resourcePath: string[]): QueryBuilder {
  return query().with({resourcePath});
}

export const queryBuilderPropType = PropTypes.shape({
  customQuery: PropTypes.func,
  defaultModel: PropTypes.func,
  done: PropTypes.func,
  fields: PropTypes.func,
  equals: PropTypes.func,
  filter: PropTypes.func,
  findOne: PropTypes.func,
  getResource: PropTypes.func,
  getResourcePromise: PropTypes.func,
  include: PropTypes.func,
  isResourcePopulated: PropTypes.func,
  isResourceReady: PropTypes.func,
  isResourceValid: PropTypes.func,
  meta: PropTypes.func,
  page: PropTypes.func,
  pageSize: PropTypes.func,
  sort: PropTypes.func,
  with: PropTypes.func,
  withMeta: PropTypes.func
});
